import _ from "lodash";
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";


export enum PollingFrequencyAdminTabEnum {
    Devices = 0,
    Tanks = 1,
    Tats = 2
}

export type PollingFrequencyAdminStateType = {
    activeTab: PollingFrequencyAdminTabEnum;
    deviceNameFilter: string;
    tankNameFilter: string;
    tatNameFilter: string;
    viewSelected: boolean;
    viewOptionState: ViewOptionStateEnum
    updating: boolean;
};

enum PollingFrequencyAdminActionType {
    ActiveTabChanged = "ACTIVE_TAB_CHANGED",
    DeviceFilterChanged = "DEVICE_FILTER_CHANGED",
    TankFilterChanged = "TANK_FILTER_CHANGED",
    TatFilterChanged = "TAT_FILTER_CHANGED",
    ViewSelectedChanged = "VIEW_SELECTED_CHANGED",
    UpdatingChanged = 'UPDATING_CHANGED',
    ResetChanged = "RESET_CHANGED",
}

type PollingFrequencyAdminAction = {
    type: PollingFrequencyAdminActionType.ActiveTabChanged;
    activeTab: PollingFrequencyAdminTabEnum;
} | {
     type: PollingFrequencyAdminActionType.DeviceFilterChanged;
     payload: string;
} | {
    type: PollingFrequencyAdminActionType.TankFilterChanged;
    payload: string;
} | {
   type: PollingFrequencyAdminActionType.TatFilterChanged;
    payload: string;
} | {
    type: PollingFrequencyAdminActionType.ViewSelectedChanged;
    payload: boolean;
} | {
    type: PollingFrequencyAdminActionType.UpdatingChanged;
    payload: boolean;
} | {
    type: PollingFrequencyAdminActionType.ResetChanged;
    payload: void;
};

const initialState: PollingFrequencyAdminStateType = {
    activeTab: PollingFrequencyAdminTabEnum.Devices,
    deviceNameFilter: '',
    tankNameFilter: '',
    tatNameFilter: '',
    viewSelected: false,
    viewOptionState: ViewOptionStateEnum.Default,
    updating: false
};

const optionsChanged = (newState: PollingFrequencyAdminStateType, 
    defaultState: PollingFrequencyAdminStateType) => 
    newState.deviceNameFilter !== defaultState.deviceNameFilter ||
    newState.tatNameFilter !== defaultState.tatNameFilter ||
    newState.viewSelected !== defaultState.viewSelected;

function pollingFrequencyAdminReducer(state: PollingFrequencyAdminStateType, action: PollingFrequencyAdminAction): PollingFrequencyAdminStateType {
    let copy = _.cloneDeep(state);    

    switch (action.type) {

        case PollingFrequencyAdminActionType.ActiveTabChanged:
            copy.activeTab = action.activeTab;
            break;

        case PollingFrequencyAdminActionType.DeviceFilterChanged:
            copy.deviceNameFilter = action.payload;
            break;

        case PollingFrequencyAdminActionType.TankFilterChanged:
            copy.tankNameFilter = action.payload;
            break;

        case PollingFrequencyAdminActionType.TatFilterChanged:
            copy.tatNameFilter = action.payload;
            break;

        case PollingFrequencyAdminActionType.ViewSelectedChanged:
            copy.viewSelected = action.payload;
            break;

        case PollingFrequencyAdminActionType.UpdatingChanged:
            copy.updating = action.payload;
            break;
    
        case PollingFrequencyAdminActionType.ResetChanged:
            copy = _.cloneDeep(initialState);
            break;

        default:
            return state;
    }

    copy.viewOptionState = optionsChanged(copy, initialState) ? 
                            ViewOptionStateEnum.Changed : 
                            ViewOptionStateEnum.Default;

    return copy;
}

export type UsePollingFrequencyAdminStateType = {
    state: PollingFrequencyAdminStateType;
    setActiveTab: (activeTab: PollingFrequencyAdminTabEnum) => void;
    setDeviceFilter: (value: string) => void;
    setTankFilter: (value: string) => void;
    setTatFilter: (value: string) => void;
    setViewSelected: (value: boolean) => void;
    setUpdating: (value: boolean) => void;
    resetState: () => void;
};

export function usePollingFrequencyAdminState(): UsePollingFrequencyAdminStateType {

    const [state, dispatch] = useReducer(pollingFrequencyAdminReducer, initialState);

    const setActiveTab = useCallback((activeTab: PollingFrequencyAdminTabEnum) => dispatch({
        type: PollingFrequencyAdminActionType.ActiveTabChanged,
        activeTab: activeTab
    }), []);

    const setDeviceFilter = useCallback((value: string) => dispatch({
        type: PollingFrequencyAdminActionType.DeviceFilterChanged,
        payload: value
    }), []);

    const setTankFilter = useCallback((value: string) => dispatch({
        type: PollingFrequencyAdminActionType.TankFilterChanged,
        payload: value
    }), []);

    const setTatFilter = useCallback((value: string) => dispatch({
        type: PollingFrequencyAdminActionType.TatFilterChanged,
        payload: value
    }), []);


    const setViewSelected = useCallback((value: boolean) => dispatch({
        type: PollingFrequencyAdminActionType.ViewSelectedChanged,
        payload: value
    }), []);

    const setUpdating = useCallback((value: boolean) => dispatch({
        type: PollingFrequencyAdminActionType.UpdatingChanged,
        payload: value
    }), []);


    const resetState = useCallback(() => dispatch({
        type: PollingFrequencyAdminActionType.ResetChanged,
        payload: undefined
    }), [])

    return {
        state,
        setActiveTab,
        setDeviceFilter,
        setTankFilter,
        setTatFilter,
        setViewSelected,
        setUpdating,
        resetState
    };
}
